import React, { useState, useEffect } from 'react';
import {
  School,
  Phone,
  Mail,
  MapPin,
  Clock,
  Calendar,
  Palette,
  Globe,
  Edit,
  UserPlus,
  Users,
  BookOpen,
  CalendarPlus,
  X,
  Save,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../../../app/providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { schoolsApi } from '../../../lib/api';

interface SchoolData {
  name: string;
  type: string;
  phone: string;
  email: string;
  workingHours: string;
  address: string;
  semesterStart: string;
  semesterEnd: string;
  primaryColor: string;
  secondaryColor: string;
  website: string;
  facebook: string;
  instagram: string;
}

interface QuickAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

export function SchoolDetailsTab() {
  const { currentSchool, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState<SchoolData>({
    name: '',
    type: 'Private School',
    phone: '',
    email: '',
    workingHours: 'Monday - Thursday, 08:00 - 17:00',
    address: '',
    semesterStart: '',
    semesterEnd: '',
    primaryColor: '#14b8a6',
    secondaryColor: '#0d9488',
    website: '',
    facebook: '',
    instagram: '',
  });

  // Load school data
  useEffect(() => {
    if (currentSchool) {
      loadSchoolData();
    } else {
      setLoading(false);
    }
  }, [currentSchool]);

  const loadSchoolData = async () => {
    if (!currentSchool?.id) return;

    try {
      setLoading(true);
      const response = await schoolsApi.get(currentSchool.id);
      const school = response.school;

      setFormData({
        name: school.name || '',
        type: school.type || 'Private School',
        phone: school.phone || '',
        email: school.email || '',
        workingHours: school.working_hours || 'Monday - Thursday, 08:00 - 17:00',
        address: school.address || '',
        semesterStart: school.semester_start || '',
        semesterEnd: school.semester_end || '',
        primaryColor: school.primary_color || '#14b8a6',
        secondaryColor: school.secondary_color || '#0d9488',
        website: school.website || '',
        facebook: school.facebook || '',
        instagram: school.instagram || '',
      });
    } catch (error) {
      console.error('Failed to load school data:', error);
      // Use currentSchool data as fallback
      setFormData(prev => ({
        ...prev,
        name: currentSchool?.name || '',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentSchool?.id) return;

    try {
      setSaving(true);
      await schoolsApi.update(currentSchool.id, {
        name: formData.name,
        type: formData.type,
        phone: formData.phone,
        email: formData.email,
        working_hours: formData.workingHours,
        address: formData.address,
        semester_start: formData.semesterStart,
        semester_end: formData.semesterEnd,
        primary_color: formData.primaryColor,
        secondary_color: formData.secondaryColor,
        website: formData.website,
        facebook: formData.facebook,
        instagram: formData.instagram,
      });

      // Refresh user data to get updated school
      if (refreshUser) {
        await refreshUser();
      }

      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save school data:', error);
      alert('Failed to save school details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const quickActions: QuickAction[] = [
    { icon: <School className="w-5 h-5" />, label: 'Manage School', onClick: () => setIsEditing(true) },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Add New Student', onClick: () => navigate('/admin/academics?tab=students&action=add') },
    { icon: <UserPlus className="w-5 h-5" />, label: 'Add New Teacher', onClick: () => navigate('/admin/academics?tab=teachers&action=add') },
    { icon: <BookOpen className="w-5 h-5" />, label: 'Add New Course', onClick: () => navigate('/admin/courses?action=add') },
    { icon: <CalendarPlus className="w-5 h-5" />, label: 'Add New Event', onClick: () => navigate('/admin/schedule?action=add') },
    { icon: <Users className="w-5 h-5" />, label: 'Manage Students', onClick: () => navigate('/admin/academics?tab=students') },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-teal-300 transition-colors"
            >
              <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600">
                {action.icon}
              </div>
              <span className="text-xs font-medium text-gray-700 text-center">{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* General Information */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-gray-900">General Information</h3>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-600 border border-teal-600 rounded-lg hover:bg-teal-50 transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit Details
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Changes
              </button>
            </div>
          )}
        </div>

        {isEditing ? (
          /* Edit Form */
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">School Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="Private School">Private School</option>
                  <option value="Public School">Public School</option>
                  <option value="Charter School">Charter School</option>
                  <option value="Online School">Online School</option>
                  <option value="Tutoring Center">Tutoring Center</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Working Hours</label>
                <input
                  type="text"
                  value={formData.workingHours}
                  onChange={(e) => setFormData({ ...formData, workingHours: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Semester Start</label>
                  <input
                    type="date"
                    value={formData.semesterStart}
                    onChange={(e) => setFormData({ ...formData, semesterStart: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Semester End</label>
                  <input
                    type="date"
                    value={formData.semesterEnd}
                    onChange={(e) => setFormData({ ...formData, semesterEnd: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                  <input
                    type="color"
                    value={formData.primaryColor}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Color</label>
                  <input
                    type="color"
                    value={formData.secondaryColor}
                    onChange={(e) => setFormData({ ...formData, secondaryColor: e.target.value })}
                    className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        ) : (
          /* Display Mode */
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600 flex-shrink-0">
                  <School className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">School Name</p>
                  <p className="font-medium text-gray-900">{formData.name || 'Not set'}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-teal-100 text-teal-700 rounded">
                    {formData.type}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-medium text-gray-900">{formData.phone || 'Not set'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">{formData.email || 'Not set'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600 flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Working Hours</p>
                  <p className="font-medium text-gray-900">{formData.workingHours || 'Not set'}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-600 flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium text-gray-900">{formData.address || 'Not set'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-600 flex-shrink-0">
                  <Calendar className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Semester Dates</p>
                  <p className="font-medium text-gray-900">
                    {formData.semesterStart && formData.semesterEnd
                      ? `${formData.semesterStart} - ${formData.semesterEnd}`
                      : 'Not set'}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center text-pink-600 flex-shrink-0">
                  <Palette className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">School Colors</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div
                      className="w-6 h-6 rounded-full border border-gray-200"
                      style={{ backgroundColor: formData.primaryColor }}
                    />
                    <div
                      className="w-6 h-6 rounded-full border border-gray-200"
                      style={{ backgroundColor: formData.secondaryColor }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
                  <Globe className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Website</p>
                  {formData.website ? (
                    <a href={formData.website} className="text-sm text-teal-600 hover:underline">
                      {formData.website}
                    </a>
                  ) : (
                    <p className="font-medium text-gray-900">Not set</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
